/**
 * 来場者ポイントサービス
 * QRポイント付与APIの呼び出しを提供します
 */

import { getSupabaseClient } from '../../../services/supabase/client';
import { AppError } from '../../../shared/utils/errorHandling';
import { logError } from '../../../shared/utils/logger';
import { isPointDistributionManualCode, normalizePointClaimToken } from '../../../shared/utils/pointClaimQr';
import { validateRequiredString } from '../../../shared/utils/validation';
import { invalidateVisitorHomeBootstrapCache } from './visitorHomeBootstrapCache';
import { POINT_DISTRIBUTION_PREFIX, visitorClaimPointDistribution } from './visitorPointDistributionService';

/** points-claim RPC名 */
const POINTS_CLAIM_RPC_NAME = 'points_claim';

/** 入力不正エラーコード */
export const VISITOR_POINTS_CLAIM_INVALID_INPUT_CODE = 'VISITOR_POINTS_CLAIM_INVALID_INPUT';

/** セッション不正エラーコード */
export const VISITOR_POINTS_CLAIM_SESSION_INVALID_CODE = 'VISITOR_POINTS_CLAIM_SESSION_INVALID';

/** トークン未検出エラーコード */
export const VISITOR_POINTS_CLAIM_TOKEN_NOT_FOUND_CODE = 'VISITOR_POINTS_CLAIM_TOKEN_NOT_FOUND';

/** トークン未発行エラーコード */
export const VISITOR_POINTS_CLAIM_TOKEN_UNAVAILABLE_CODE = 'VISITOR_POINTS_CLAIM_TOKEN_UNAVAILABLE';

/** トークン期限切れエラーコード */
export const VISITOR_POINTS_CLAIM_TOKEN_EXPIRED_CODE = 'VISITOR_POINTS_CLAIM_TOKEN_EXPIRED';

/** トークン利用上限エラーコード */
export const VISITOR_POINTS_CLAIM_TOKEN_EXHAUSTED_CODE = 'VISITOR_POINTS_CLAIM_TOKEN_EXHAUSTED';

/** トークン無効化エラーコード */
export const VISITOR_POINTS_CLAIM_TOKEN_INACTIVE_CODE = 'VISITOR_POINTS_CLAIM_TOKEN_INACTIVE';

/** イベント上限到達エラーコード */
export const VISITOR_POINTS_CLAIM_LIMIT_EXCEEDED_CODE = 'VISITOR_POINTS_CLAIM_LIMIT_EXCEEDED';

/** ポイントルール未設定エラーコード */
export const VISITOR_POINTS_CLAIM_RULE_NOT_FOUND_CODE = 'VISITOR_POINTS_CLAIM_RULE_NOT_FOUND';

/** イベント対象外エラーコード */
export const VISITOR_POINTS_CLAIM_EVENT_UNAVAILABLE_CODE = 'VISITOR_POINTS_CLAIM_EVENT_UNAVAILABLE';

/** ポイント取得失敗エラーコード */
export const VISITOR_POINTS_CLAIM_FAILED_CODE = 'VISITOR_POINTS_CLAIM_FAILED';

/** 読み取り集中（レート制限超過）エラーコード */
export const VISITOR_POINTS_CLAIM_RATE_LIMITED_CODE = 'VISITOR_POINTS_CLAIM_RATE_LIMITED';

/**
 * RPCエラーに特定メッセージが含まれるか判定する
 * @param {{ message?: string } | null} rpcError - RPCエラー
 * @param {string} errorKeyword - 判定キーワード
 * @returns {boolean} 判定結果
 */
const includesRpcErrorMessage = (rpcError, errorKeyword) => {
  if (!rpcError?.message) {
    return false;
  }

  return rpcError.message.includes(errorKeyword);
};

/**
 * points-claim RPCエラーをアプリエラーコードに変換する
 * @param {{ message?: string } | null} rpcError - RPCエラー
 * @returns {string} アプリエラーコード
 */
const mapPointsClaimRpcErrorCode = (rpcError) => {
  if (
    includesRpcErrorMessage(rpcError, 'INVALID_SESSION_TOKEN')
    || includesRpcErrorMessage(rpcError, 'SESSION_NOT_FOUND')
    || includesRpcErrorMessage(rpcError, 'VISITOR_INACTIVE')
  ) {
    return VISITOR_POINTS_CLAIM_SESSION_INVALID_CODE;
  }

  if (
    includesRpcErrorMessage(rpcError, 'INVALID_REWARD_TOKEN')
    || includesRpcErrorMessage(rpcError, 'REWARD_TOKEN_NOT_FOUND')
  ) {
    return VISITOR_POINTS_CLAIM_TOKEN_NOT_FOUND_CODE;
  }

  if (includesRpcErrorMessage(rpcError, 'REWARD_TOKEN_NOT_ISSUED')) {
    return VISITOR_POINTS_CLAIM_TOKEN_UNAVAILABLE_CODE;
  }

  if (includesRpcErrorMessage(rpcError, 'REWARD_TOKEN_EXPIRED')) {
    return VISITOR_POINTS_CLAIM_TOKEN_EXPIRED_CODE;
  }

  if (includesRpcErrorMessage(rpcError, 'REWARD_TOKEN_EXHAUSTED')) {
    return VISITOR_POINTS_CLAIM_TOKEN_EXHAUSTED_CODE;
  }

  if (includesRpcErrorMessage(rpcError, 'REWARD_TOKEN_INACTIVE')) {
    return VISITOR_POINTS_CLAIM_TOKEN_INACTIVE_CODE;
  }

  if (includesRpcErrorMessage(rpcError, 'EVENT_CLAIM_LIMIT_EXCEEDED')) {
    return VISITOR_POINTS_CLAIM_LIMIT_EXCEEDED_CODE;
  }

  if (includesRpcErrorMessage(rpcError, 'POINT_RULE_NOT_FOUND')) {
    return VISITOR_POINTS_CLAIM_RULE_NOT_FOUND_CODE;
  }

  if (includesRpcErrorMessage(rpcError, 'EVENT_NOT_PUBLISHED')) {
    return VISITOR_POINTS_CLAIM_EVENT_UNAVAILABLE_CODE;
  }

  if (includesRpcErrorMessage(rpcError, 'RATE_LIMIT_EXCEEDED')) {
    return VISITOR_POINTS_CLAIM_RATE_LIMITED_CODE;
  }

  return VISITOR_POINTS_CLAIM_FAILED_CODE;
};

/**
 * points-claim の単一行レスポンスを検証する
 * @param {any} rpcRow - RPC戻り値の行データ
 * @returns {{
 * claimed: boolean,
 * visitorId: string,
 * eventId: string,
 * eventTitle: string,
 * pointsGranted: number,
 * currentPoint: number,
 * maxReachedPoint: number,
 * pointLogId: string,
 * }} ポイント取得結果
 * @throws {AppError} レスポンス不正の場合
 */
const validatePointClaimRow = (rpcRow) => {
  /** claimed */
  const claimed = Boolean(rpcRow?.claimed);

  /** visitorId */
  const visitorId = rpcRow?.visitor_id;

  /** eventId */
  const eventId = rpcRow?.event_id;

  /** eventTitle */
  const eventTitle = String(rpcRow?.event_title ?? '').trim();

  /** pointsGranted */
  const pointsGranted = Number(rpcRow?.points_granted);

  /** currentPoint */
  const currentPoint = Number(rpcRow?.current_point);

  /** maxReachedPoint */
  const maxReachedPoint = Number(rpcRow?.max_reached_point ?? 0);

  /** pointLogId */
  const pointLogId = rpcRow?.point_log_id;

  if (
    !claimed
    || !visitorId
    || !eventId
    || Number.isNaN(pointsGranted)
    || Number.isNaN(currentPoint)
    || Number.isNaN(maxReachedPoint)
    || !pointLogId
  ) {
    throw new AppError('points-claim のレスポンス形式が不正です。', {
      code: VISITOR_POINTS_CLAIM_FAILED_CODE,
      statusCode: 500,
      details: { rpcRow },
    });
  }

  return {
    claimed,
    visitorId,
    eventId,
    eventTitle,
    pointsGranted,
    currentPoint,
    maxReachedPoint,
    pointLogId,
  };
};

/**
 * QRトークンまたは8文字手入力コードでポイントを取得する
 * @param {string} sessionToken - 来場者セッショントークン
 * @param {string} rewardToken - ポイント付与トークンまたは8文字コード
 * @returns {Promise<{
 * claimed: boolean,
 * visitorId: string,
 * eventId: string,
 * eventTitle: string,
 * pointsGranted: number,
 * currentPoint: number,
 * maxReachedPoint: number,
 * pointLogId: string,
 * }>} ポイント取得結果
 * @throws {AppError} 取得に失敗した場合
 */
export const visitorPointsClaim = async (sessionToken, rewardToken) => {
  // 配布QRは企画・アンケートへ流さず、専用の回数制限と冪等処理へ接続する。
  if (String(rewardToken ?? '').trim().startsWith(POINT_DISTRIBUTION_PREFIX) || isPointDistributionManualCode(rewardToken)) {
    return visitorClaimPointDistribution(String(sessionToken ?? ''), String(rewardToken).trim());
  }
  /** セッショントークン検証結果 */
  const sessionTokenValidation = validateRequiredString(sessionToken, 'セッショントークン');

  if (!sessionTokenValidation.isValid) {
    throw new AppError(sessionTokenValidation.message, {
      code: VISITOR_POINTS_CLAIM_INVALID_INPUT_CODE,
      statusCode: 400,
    });
  }

  /** QRトークンまたは大文字へ正規化した8文字コード */
  const normalizedRewardToken = normalizePointClaimToken(rewardToken);

  /** ポイント付与トークン検証結果 */
  const rewardTokenValidation = validateRequiredString(
    normalizedRewardToken,
    'ポイント付与コード',
  );

  if (!rewardTokenValidation.isValid) {
    throw new AppError(rewardTokenValidation.message, {
      code: VISITOR_POINTS_CLAIM_INVALID_INPUT_CODE,
      statusCode: 400,
    });
  }

  /** Supabaseクライアント */
  const supabaseClient = getSupabaseClient();

  /** RPC結果 */
  const { data, error } = await supabaseClient.rpc(POINTS_CLAIM_RPC_NAME, {
    p_session_token: sessionToken.trim(),
    p_token: normalizedRewardToken,
  });

  if (error) {
    /** 変換済みエラーコード */
    const mappedErrorCode = mapPointsClaimRpcErrorCode(error);

    logError('points-claim RPC 実行に失敗しました。', {
      code: error.code,
      message: error.message,
      details: error.details,
      mappedErrorCode,
    });

    throw new AppError('points-claim の実行に失敗しました。', {
      code: mappedErrorCode,
      statusCode: mappedErrorCode === VISITOR_POINTS_CLAIM_FAILED_CODE ? 500 : 400,
      details: {
        message: error.message,
      },
    });
  }

  // コードが見つからない時、DBは試行回数をcommitさせるため例外ではなく0行を返す（旧DBは例外で返す）。
  if (Array.isArray(data) && data.length === 0) {
    throw new AppError('pt付与コードが見つかりません。', {
      code: VISITOR_POINTS_CLAIM_TOKEN_NOT_FOUND_CODE,
      statusCode: 400,
    });
  }

  /** 単一行のRPC結果 */
  const rpcRow = Array.isArray(data) ? data[0] ?? null : data ?? null;

  // 取得済み・期限切れなど、コードが見つかった後の失敗も、DBは試行回数をcommitさせるため
  // claimed = false と failure_code（旧DBの例外と同じ文字列）の行で返す（旧DBは例外で返す）。
  if (rpcRow?.claimed === false && typeof rpcRow.failure_code === 'string') {
    /** 変換済みエラーコード */
    const mappedErrorCode = mapPointsClaimRpcErrorCode({ message: rpcRow.failure_code });

    throw new AppError('points-claim の実行に失敗しました。', {
      code: mappedErrorCode,
      statusCode: mappedErrorCode === VISITOR_POINTS_CLAIM_FAILED_CODE ? 500 : 400,
      details: {
        message: rpcRow.failure_code,
      },
    });
  }

  const claimResult = validatePointClaimRow(rpcRow);

  // pt残高が変わるため、ホーム集約データのTTLキャッシュを破棄し古いptを表示し続けないようにする
  invalidateVisitorHomeBootstrapCache(sessionToken.trim());

  return claimResult;
};
